import React, { useState } from 'react';

// Interface for email record data
interface EmailRecord {
  campaign: string;
  fromEmail: string;
  sentAt: string;
  status: 'Delivered' | 'Not Delivered' | 'Bounced';
}

interface EmailInitiatedTableProps {
  records: EmailRecord[];
  itemsPerPage?: number; // Optional prop for items per page, defaults to 6
}

// Component to display a table of initiated emails
const EmailInitiatedTable: React.FC<EmailInitiatedTableProps> = ({
  records,
  itemsPerPage = 6,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate pagination
  const totalPages = Math.ceil(records.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRecords = records.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className='bg-white rounded-lg border border-gray-200 shadow-sm mt-6 p-6'>
      <h3 className='text-[18px] font-semibold mb-3 text-[#3F5265]'>
        Email Initiated
      </h3>
      <table className='w-full border text-sm'>
        <thead className='bg-gray-50 text-[#657584] text-[12px]'>
          <tr>
            <th className='px-6 py-4 text-left font-normal'>CAMPAIGNS</th>
            <th className='px-6 py-4 text-left font-normal'>FROM EMAIL</th>
            <th className='px-6 py-4 text-left font-normal'>SENT AT</th>
            <th className='px-6 py-4 text-left font-normal'>STATUS</th>
          </tr>
        </thead>
        <tbody className='text-[#1F2937] text-[14px]'>
          {currentRecords.map((record, index) => (
            <tr key={index} className='border-t'>
              <td className='px-6 py-4'>
                {record.campaign}
                <div className='text-xs text-gray-400'>{'Subject line:'}</div>
              </td>
              <td className='px-6 py-4'>{record.fromEmail}</td>
              <td className='px-6 py-4'>{record.sentAt}</td>
              <td
                className={`px-6 py-4 ${
                  record.status === 'Delivered'
                    ? 'text-green-600'
                    : record.status === 'Not Delivered'
                    ? 'text-red-500'
                    : 'text-[#ED900F]'
                }`}
              >
                <span
                  className={`px-3 py-1 rounded-full ${
                    record.status === 'Delivered'
                      ? 'bg-[#ECFDF5]'
                      : record.status === 'Not Delivered'
                      ? 'bg-[#FCECEC]'
                      : 'bg-[#FFF7E6]'
                  } rounded-[40px]`}
                >
                  {record.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination controls, shown only if needed */}
      {records.length > itemsPerPage && (
        <div className='flex justify-end mt-4 gap-2'>
          <button
            className='px-3 py-1 border rounded disabled:opacity-50'
            onClick={() => setCurrentPage((prev) => prev - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              className={`px-3 py-1 border rounded ${
                currentPage === idx + 1
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-600'
              }`}
              onClick={() => setCurrentPage(idx + 1)}
            >
              {idx + 1}
            </button>
          ))}
          <button
            className='px-3 py-1 border rounded disabled:opacity-50'
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailInitiatedTable;
